import type { TravelDirection } from "@seagames/contracts";
import { z } from "zod";

/**
 * M9b: the old A&D URLs (`/logistics/arrival-departure?tab=arrivals|departures`
 * plus the list's filters) still arrive from bookmarks and shared links. The
 * layout route redirects them to the Journeys list, carrying the filters the
 * old list had. `tab=overview` (or none) stays on the Overview.
 */
export const LEGACY_TABS = ["overview", "arrivals", "departures"] as const;
export type LegacyTab = (typeof LEGACY_TABS)[number];

const DIRECTION_OF: Record<Exclude<LegacyTab, "overview">, TravelDirection> = { arrivals: "ARRIVAL", departures: "DEPARTURE" };

/** The old list's params, each dropped when it does not parse (they were lenient then too). */
const carried = {
	games_id: z.string().uuid(),
	from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
	to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
	entry_point_id: z.string().uuid(),
	responsible_organization_id: z.string().uuid(),
	status: z.string().max(200),
	q: z.string().trim().min(1).max(100),
} as const;

export interface LegacyJourneysSearch {
	direction: TravelDirection;
	games_id?: string;
	from?: string;
	to?: string;
	entry_point_id?: string;
	responsible_organization_id?: string;
	status?: string;
	q?: string;
}

/** Where an old `?tab=` URL goes: the Journeys list's search, or null to stay (Overview / no tab / unknown tab). */
export function legacyJourneysSearch(tab: unknown, raw: Record<string, unknown>): LegacyJourneysSearch | null {
	if (tab !== "arrivals" && tab !== "departures") return null;
	const out: LegacyJourneysSearch = { direction: DIRECTION_OF[tab] };
	for (const [key, schema] of Object.entries(carried) as Array<[keyof typeof carried, z.ZodTypeAny]>) {
		const v = schema.safeParse(raw[key]);
		if (v.success) out[key] = v.data as string;
	}
	return out;
}
